import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { Stack, Box } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MultipleSelectOption } from "../../atoms/MultiSelect/MultipleSelectOption"; 

import { createDoctorAssistant, getDoctorAssistantByID, updateDoctorAssistant } from "../../../Api/DoctorAssistant/DoctorAssistant.Api";
import { getAllDoctorsAndPromoCodeTypes } from "../../../Api/Signin/Transaction.Api";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { RemoteSelectMultiple } from "../../atoms/Input/RemoteSelectMultiple";
import { RemoteSelect } from "../../atoms/Input/RemoteSelect";
import { TextInput } from "../../atoms/Input/TextInput";
import { PhoneNumber } from "../../atoms/Input/PhoneNumber";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { ReactComponent as InfoIconGrey } from "../../../Assets/Images/InfoIconGrey.svg";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { schema } from "./shema";
import { ValidationPopper } from "../../molecules/ValidationPopper/ValidationPopper";

import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { useAuth } from "../../../Store/AuthContext";
import { DoctorAssitant } from "../../../Constants/AppContants";
import { NoRecordsFound } from "../../atoms/NoRecordsFound/NoRecordsFound";
import {
  findUserNameDuplicateError,
  USER_NAME_ALREADY_EXISTS_VALIDATION_KEY,
} from "../../../Utils/HandleError/userNameDuplicateError";
import { validateUsername } from "../../../Utils/validateUsername";

export const AddDoctorAssistant = ({
  close,
  closeMain,
  data,
  OpenDrawer,
  loadDoctorAssitants,
  mode,
  refetch,
}) => {
  const { state } = useAuth();
  const { t } = useTranslation();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const { closeOverlay, OverlayState, openOverlay } = useOverlayState();


  // Trigger server error
  const [isError, setIsError] = useState(false);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // State to hold fetched doctors data for the multi-select
  const [doctors, setDoctors] = useState([]);
  const [isDisabled, setDisabled] = useState(false);
   const [errors, setErrors] = useState({
      visible: false,
      type: undefined
    })

   // Username field focus state
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [isInfoIconClicked, setIsInfoIconClicked] = useState(false);
  const [isUsernameInvalid, setIsUsernameInvalid] = useState(false);

  // Set up react-hook-form with only the needed fields
  const {
    control,
    setValue,
    watch,
    setError,
    reset,
    handleSubmit,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      fullName: "",
      userName: "",
      email: "",
      phoneNumber: "",
      roleName: "DoctorAssistant",
      doctorIds: [],
      isActive: "active", // Default status set as "active"
    },
    mode: "onChange",
    resolver: yupResolver(schema(t, mode)),
    criteriaMode: "all",
    reValidateMode: "onChange",
  });

  const currentUserName = watch("userName") || "";
  const isUsernameValid = validateUsername(currentUserName);

  const showUsernameRules = isInfoIconClicked || (isUsernameFocused && !isUsernameValid);

  const selectedDoctors = watch("doctorIds") || [];

  const GetDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      const response = await getAllDoctorsAndPromoCodeTypes();
      if (response.type === 0) {
        const standardizedDoctors = response.data.doctors.map((doctor) => ({
          ...doctor,
          id: doctor.id.toLowerCase(), // Ensure consistent casing
        }));
        setDoctors(standardizedDoctors);
      }
    } catch (exception) {
      console.error("Error fetching doctors:", exception);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Fetch doctor assistant data by ID for update mode
  const getDoctorAssistantsByID = async () => {
    try {
      
     
      startLoading({ btnName: "default" });
      const response = await getDoctorAssistantByID({
        id: data.userId,
        lang: state.lang.code,
      });
      if (response.type === 0) {
        // Prepopulate the form with response data and doctor IDs from data.doctors

        data = response.data;
        setDisabled(data.isResetPassword);
        const doctorIds = Array.isArray(data.doctors)
          ? data.doctors.map((doctor) => doctor.Id.toLowerCase()) // Standardize IDs
          : [];
        reset({
          fullName: response.data.fullName,
          userName: response.data?.userName || response.data?.UserName || "",
          email: response.data.email,
          phoneNumber: response.data.phoneNumber,
          phoneNumberCountryCode: response.data.phoneNumberCountryCode, 
          phoneNumberDialCode: response.data.phoneNumberDialCode,       
          isActive: response.data.isActive ? "active" : "inactive",
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          doctorIds,
        });
      }
      stopLoading({ btnName: "default" });
    } catch (exception) {
      console.error("Error fetching doctor assistant:", exception);
      stopLoading({ btnName: "default" });
    }
  };

  // Update the selected doctor IDs when user selects/deselects
  const handleOnSelectDeselectAll = (doctorIds) => {
    setValue("doctorIds", doctorIds);
  };

  const handleUserNameResponseError = (messageOrMessages) => {
    if (!findUserNameDuplicateError(messageOrMessages)) {
      return false;
    }

    setError("userName", {
      type: "manual",
      message: t(USER_NAME_ALREADY_EXISTS_VALIDATION_KEY),
    }, { shouldFocus: true });
    return true;
  };

  const handleSupportResponseError = (message) => {
    if (typeof message !== "string") {
      return false;
    }

    if (
      message !== "Doctor assistant is not linked to an auth user account. Please contact support." &&
      message !== "Linked doctor assistant user account not found. Please contact support."
    ) {
      return false;
    }

    setError("userName", {
      type: "manual",
      message,
    }, { shouldFocus: true });
    setErrorMessage(message);
    setIsError(true);
    return true;
  };

  // Handler for form submission to create a doctor assistant
  const createProfileHandler = async (values) => {
    if (isError) {
      setIsError(false);
      setErrorMessage("");
    }

    const trimmedUserName = values.userName?.trim() ?? "";
    if (!trimmedUserName) {
      setError("userName", {
        type: "manual",
        message: t("validation:required-field"),
      }, { shouldFocus: true });
      return;
    }

    const isUsernameValid = validateUsername(trimmedUserName);

    if (!isUsernameValid) {
      setIsUsernameInvalid(true);
      setError("userName", {
        type: "manual",
        message: "",
      }, { shouldFocus: true });
      return;
    }

    startLoading({ btnName: "create" });
    try {
      const formattedDoctorIds = (values.doctorIds || []).map((doc) =>
        typeof doc === "object" ? doc.id : doc
      );

      const cleanValues = { ...values };
      delete cleanValues.userName;

      const response = await createDoctorAssistant({
        doctorassistant: {
          ...cleanValues,
          doctorIds: formattedDoctorIds,
          UserName: trimmedUserName,
          language: state.lang.code,
        },
      });

      if (response.type === 0) {
        openOverlay({
          OverlayName: "reset-password",
          mode: "email-sent",
        });
      } else if (response.type === 2) {
        if (response.message === "email-exist" || response.message === "admin-email-exist") {
          setError("email", {
            message: t(`validation:${response.message}`),
          });
        } else if (response.message === "phone-exist" || response.message === "admin-phone-exist") {
          setError("phoneNumber", {
            message: t(`validation:${response.message}`),
          });
        } else if (response.message === "email-not-sent") {
          openOverlay({
            OverlayName: "reset-password",
            mode: "fail-email-not-sent",
          });
        } else if (handleUserNameResponseError([response.message, ...(Array.isArray(response.data) ? response.data : [])])) {
          return;
        } else if (response.message === "Admin creation failed"){
          setErrorMessage("");
          setIsError(true)
        }
      } else {
        setErrorMessage("");
        setIsError(true);
      }
    } catch (error) {
      console.error("Error creating doctor assistant:", error);
      setErrorMessage("");
      setIsError(true);
    } finally {
      stopLoading();
    }
  };

    const updateProfileHandler = async (values) => {
      if (errors.visible) {
        setErrors({ visible: false, type: undefined })
      }
      if (isError) {
        setIsError(false);
        setErrorMessage("");
      }

      const trimmedUserName = values.userName?.trim() ?? "";
      if (!trimmedUserName) {
        setError("userName", {
          type: "manual",
          message: t("validation:required-field"),
        }, { shouldFocus: true });
        return;
      }

      const isUsernameValid = validateUsername(trimmedUserName);

    if (!isUsernameValid) {
      setIsUsernameInvalid(true);
      setError("userName", {
        type: "manual",
        message: "",
      }, { shouldFocus: true });
      return;
    }

      startLoading({ btnName: "update" });
      const formattedDoctorIds = (values.doctorIds || []).map((doc) =>
        typeof doc === "object" ? doc.id : doc
      );

      const cleanValues = { ...values };
      delete cleanValues.userName;

      await updateDoctorAssistant({
        doctorAssistant: {
          ...cleanValues,
          doctorIds: formattedDoctorIds,
          UserName: trimmedUserName,
          UserId: data.userId,
          Id: data.id,
          RoleName: DoctorAssitant,
          language: state.lang.code,
        },
      }).then((d) => {
        if (d.type === 3 && d?.message === "server-error") {
          setErrors({ visible: true, type: d?.message })
          openOverlay({
            OverlayName: "reset-password",
            mode: "fail-email-not-sent",
          });
          return;
        } 

        if (d.message === "email-sent") {
          openOverlay({ OverlayName: "email-sent" });
        }

        if(d.message === "not-affected"){
          close();
        }

        if(d.message === "Success"){
          close();
        }

        if (d.type === 0) {
          reset();
          loadDoctorAssitants();
        } else if (d.type === 2) {
          if (d?.data && d?.data[0] === "server-error") {
            setErrors({ visible: true, type: d?.data[0] })
            return;
          }
          if (d?.message === "email-exist" || d?.message === "admin-email-exist") {
            setError("email", {
              type: "focus",
              message: t(`validation:${d.message}`),
            });
          }
          if (d?.message === "phone-exist" || d?.message === "admin-phone-exist") {
            setError("phoneNumber", {
              type: "focus",
              message: t(`validation:${d.message}`),
            });
          }
          if (handleUserNameResponseError([d?.message, ...(Array.isArray(d?.data) ? d.data : [])]) || handleSupportResponseError(d?.message)) {
            return;
          }
        }
      });
      stopLoading();
    };

  // useEffect to fetch doctors and populate form in update mode
  useEffect(() => {
    const fetchData = async () => {
      await GetDoctors(); // Fetch doctors first
      if (mode === "update" && data) {
        await getDoctorAssistantsByID(); // Then populate the form
      }
    };
    fetchData();
  }, [mode, data]);

  // Options for the "Status" field
  const ActiveStatus = [
    { id: "active", name: "Active" },
    { id: "inactive", name: "Inactive" },
  ];

  return (
    <SideDrawer
      isLoading={LoadingState.visible}
      headingText={
        mode === "update"
          ? t("glossary:add-doctor-assistant.updatedoctorassistant")
          : t("glossary:add-doctor-assistant.new-doctor-assistant-title")
      }
      width={"690px"}
      openDrawer={OpenDrawer}
      close={close}
      anchorDrawer={"right"}
    >

     {isError && (
        <NoRecordsFound
          isError={isError}
          textPosition={"start"}
          padding={"8px"}
          textColor={"black"}
          label={errorMessage || t("validation:server-error")}
        />
      )}

      <div style={{ paddingBottom: "20px",marginTop: "20px" }}>
        {OverlayState.visible && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail-email-not-sent"}
            title={t("user.action.reset-password", { ns: "glossary" })}
            description={
              OverlayState.mode === "fail-email-not-sent"
                ? t("validation:reset-password-link-send-fail")
                : t("user.action.reset-password-desc", { ns: "glossary" })
            }
            open={OverlayState.visible}
            close={() => closeOverlay()}
            onOkClick={() => {
              reset();
              loadDoctorAssitants();
              close();
              closeOverlay();
            }}
          />
        )}
        <form
          onSubmit={handleSubmit((formData) => {
            if (mode === "update") {
              // Placeholder for update logic if needed in the future
              updateProfileHandler(formData);

            } else {
              createProfileHandler(formData);
            }
          })}
          onChange={() => {
            if (errors.visible) {
              setErrors({ visible: false, type: undefined });
            }
            if (isUsernameInvalid) {
              setIsUsernameInvalid(false);
            }
          }}
        >
          <Stack rowGap={"20px"} width={"640px"}>
              {/* Full Name Input */}
              <TextInput
                required={true}
                name={"fullName"}
                control={control}
                label={"Full name"}
              />
              
              <Box 
                position="relative" 
                onFocusCapture={() => setIsUsernameFocused(true)}
                onBlurCapture={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsUsernameFocused(false);
                    setIsInfoIconClicked(false);
                  }
                }}
              >
              <TextInput
                required={false}
                name={"userName"}
                control={control}
                label={
                      <Box display="inline-flex" alignItems="center">
                        <span>{t("glossary:doctor-details.user-name")}</span>
                        <span style={{ color: "#FF0000", marginLeft: "4px", marginRight: "6px" }}>*</span>
                        <InfoIconGrey 
                          style={{ width: "16px", height: "16px", cursor: "pointer" }} 
                          onClick={(e) => {
                            e.preventDefault();
                            setIsInfoIconClicked((prev) => !prev);
                          }}
                        />
                      </Box>
                    }
                maxLength={30}
              />
              
              <ValidationPopper open={showUsernameRules} value={currentUserName} absolute={true} />
              </Box>

              {/* Email Input */}
              <TextInput
                required={true}
                name={"email"}
                placeholder={t("common:placeholder.email")}
                control={control}
                label={t("common:form.email")}
                disabled={isDisabled}
              />

              {/* Mobile / Phone Number Input */}
              <PhoneNumber
                values={watch()}
                setValue={setValue}
                required={true}
                width={"220px"}
                name={"phoneNumber"}
                placeholder={"XX XXXX XXXX"}
                control={control}
                label={t("common:form.mobileNoFirstOnly")}
                disabled={isDisabled}
              />

              {/* Assigned Doctors - Multiple Selection */}
              <MultipleSelectOption
                control={control}
                label={t("glossary:add-doctor-assistant.Assigndoctors")}
                placeholder={t("glossary:finance-add-promo-code.placeholder.select-doctors")}
                name={"doctorIds"}
                options={doctors}
                getOptionLabel={(option) => option?.name || ""}
                menuItemKey={"id"}
                fullWidth={true}
                required={true}
                showSelectAll={true}
                loading={isLoadingDoctors} 
                customSearch={true} 
                value={selectedDoctors.map((doc) =>
                  typeof doc === "string"
                    ? doctors.find((d) => d.id === doc) || { id: doc, name: "" }
                    : doc
                )}
              />

              {/* Status Selector */}
              <RemoteSelect
                control={control}
                label={"Status"}
                placeholder={"Select the Status"}
                name={"isActive"}
                menuItemLabel={"name"}
                menuItemKey={"id"}
                options={ActiveStatus}
                fullWidth={false}
                required={false}
                height={"50px"}
                disabled={true}
              />

            <FormItemsWrap
              direction={"row"}
              justifyContent={"flex-end"}
              gap={"16px"}
            >
              <Button variant="whiteSquare" onClick={close}>
                {t("common:button.cancel")}
              </Button>
              <LoadingButtonType
                variant="primaryBlue"
                type="submit"
                loading={LoadingState.visible}
              >
                {mode === "update"
                  ? t("glossary:add-doctor.updateprofile")
                  : t("glossary:add-doctor.createprofile")}
              </LoadingButtonType>
            </FormItemsWrap>
          </Stack>
        </form>
      </div>
    </SideDrawer>
  );
};
